import { approachSteps } from '@/mocks/portfolio';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useState } from 'react';
import SlotText from '@/components/feature/SlotText';

export default function Approach() {
  const { ref, revealed } = useScrollReveal();
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  return (
    <section id="approach" className="py-20 md:py-28">
      <div className="mx-auto max-w-[1280px] px-6 md:px-10">
        {/* Header */}
        <div
          ref={ref}
          className={`mb-14 transition-all duration-1000 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[oklch(0.78_0.19_48)]" />
            <span className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500">
              My Approach / How I Think
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-6">
              <h2 className="font-heading fw-bold text-[clamp(32px,4.5vw,60px)] text-foreground-950 leading-[0.93] tracking-[-0.04em]">
                Business Issue
                <br />
                <span className="text-foreground-400 fw-medium">to</span>{' '}
                <SlotText text="Experience" as="span" className="fw-medium marker-yellow" color="oklch(0.09 0.01 70)" scrambleColor="rainbow" />
                <br />
                &amp; Communication
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-foreground-700 text-base leading-relaxed">
                マーケティング分析から課題抽出、戦略立案、コミュニケーション設計、クリエイティブ開発、実施・効果検証まで。6段階のアプローチとして統合し、課題の構造化から実装まで一貫して扱います。
              </p>
            </div>
          </div>
        </div>

        {/* 6 steps accordion */}
        <div
          className={`space-y-px bg-background-300 border border-background-300 transition-all duration-1000 delay-200 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {approachSteps.map((s, idx) => (
            <div
              key={s.no}
              className={`bg-background-50 transition-colors duration-500 ease-out cursor-pointer group ${
                  idx !== 0 ? 'border-t border-background-300' : ''
                } ${openIdx === idx ? 'bg-primary-200' : 'hover:bg-primary-100'}`}
              onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
              onMouseEnter={() => setOpenIdx(idx)}
            >
              <div className="grid grid-cols-12 items-start gap-4 px-6 md:px-8 py-6 md:py-7">
                <div className="col-span-2 md:col-span-1 pt-0.5">
                  <span
                    className={`font-label text-[11px] tracking-[0.08em] ${openIdx === idx ? 'text-foreground-950' : 'text-foreground-400'}`}
                  >
                    {s.no}
                  </span>
                </div>
                <div className="col-span-10 md:col-span-3">
                  <SlotText
                    text={s.title}
                    as="p"
                    className={`font-heading text-xl md:text-2xl leading-tight tracking-[-0.02em] inline-block origin-left transition-transform duration-300 hover:scale-110 ${openIdx === idx ? 'text-foreground-950' : ''}`}
                    scrambleColor="oklch(0.78 0.19 48)"
                  />
                </div>
                <div className="col-span-12 md:col-span-4 md:col-start-5">
                  <p className={`text-sm leading-relaxed ${openIdx === idx ? 'text-foreground-950' : 'text-foreground-600'}`}>
                    {s.subtitle}
                  </p>
                </div>
                <div className="col-span-12 md:col-span-3 md:col-start-10 flex items-center justify-between md:justify-end">
                  <span className="hidden md:block" />
                  <i
                    className={`ri-arrow-down-s-line text-lg w-5 h-5 flex items-center justify-center text-foreground-400 transition-transform duration-300 ${openIdx === idx ? 'rotate-180 text-foreground-950' : ''}`}
                  />
                </div>
              </div>
              {openIdx === idx && (
                <div className="px-6 md:px-8 pb-6 md:pb-7 grid grid-cols-12">
                  <div className="col-span-12 md:col-span-7 md:col-start-5">
                    <p className="text-sm md:text-base text-foreground-950 leading-relaxed border-l-2 border-foreground-950/20 pl-4">
                      {s.desc}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div
          className={`mt-10 flex flex-wrap items-center gap-4 transition-all duration-1000 delay-300 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500">
            From Analysis to <span className="marker-yellow text-foreground-950">Activation</span> —
          </p>
          <p className="text-sm text-foreground-700">
            マーケティング分析・課題抽出・戦略策定・デザイン設計・クリエイティブ開発・実施展開・効果検証。このプロセス全体を統合的に扱います。
          </p>
        </div>
      </div>
    </section>
  );
}